import type { ComponentType } from 'svelte'
import { formatter, type Formatter } from './format'
import type { Accent, LayoutSize, ReportData, ReportOptions, ReportSize, ReportType } from './types'

// The report image sizes, all at the same text scale (1080px on the short side). Must match
// ReportTemplates::SIZES on the server. Square is the default, and suits Strava. `bestFor` is the
// platform hint shown with the size; `shareLabel` names it in the share popup, where it's picked
// by where the image is going.
export const REPORT_SIZES: Record<ReportSize, { width: number, height: number, label: string, ratio: string, bestFor: string, shareLabel: string }> = {
  square: { width: 1080, height: 1080, label: 'Square', ratio: '1:1', bestFor: 'Best for Strava', shareLabel: 'Square' },
  portrait: { width: 1080, height: 1350, label: 'Portrait', ratio: '4:5', bestFor: 'Best for Instagram posts', shareLabel: 'Post' },
  landscape: { width: 1620, height: 1080, label: 'Landscape', ratio: '3:2', bestFor: 'Best for X and Facebook', shareLabel: 'X / Facebook' },
  story: { width: 1080, height: 1920, label: 'Story', ratio: '9:16', bestFor: 'Best for Instagram and Facebook Stories', shareLabel: 'Story' },
}
export const SIZE_ORDER: ReportSize[] = ['square', 'portrait', 'landscape', 'story']
// The share popup leads with the Instagram sizes.
export const SHARE_SIZE_ORDER: ReportSize[] = ['portrait', 'story', 'square', 'landscape']
export const SHARE_DEFAULT_SIZE: ReportSize = 'portrait'

// A story draws the portrait layout inside a 9:16 frame, clear of the Stories UI: the profile
// bar at the top and the reply box at the bottom. The background fills the whole frame.
const STORY_SAFE_TOP = 250
const STORY_LAYOUT: LayoutSize = 'portrait'
export const DEFAULT_SIZE: ReportSize = 'square'

export function sizeOf(options?: Partial<ReportOptions> | null) {
  const size = options?.size && REPORT_SIZES[options.size] ? options.size : DEFAULT_SIZE
  return { size, ...REPORT_SIZES[size] }
}

export const ACCENTS: Record<Accent, { label: string, color: string }> = {
  teal: { label: 'Xert teal', color: '#3cc7c4' },
  orange: { label: 'Orange', color: '#ff6a2b' },
  blue: { label: 'Blue', color: '#4c8dff' },
  purple: { label: 'Purple', color: '#a970ff' },
  red: { label: 'Red', color: '#ff4d5e' },
  green: { label: 'Green', color: '#3ddc84' },
  gold: { label: 'Gold', color: '#f5c542' },
  mono: { label: 'White', color: '#f4f6f8' },
}

export const MEDAL_COLORS = {
  gold: { main: '#f5c542', light: '#ffe58a', dark: '#b8860b', label: 'Gold' },
  silver: { main: '#cfd8e0', light: '#f4f8fb', dark: '#8a96a3', label: 'Silver' },
  bronze: { main: '#d98b4a', light: '#f3b884', dark: '#8f5426', label: 'Bronze' },
  near: { main: '#9aa7b4', light: '#c9d3dc', dark: '#5d6873', label: 'Near' },
}

// Xert's XSS colours: low (blue), high (orange), peak (red).
export const XSS_COLORS = { xlss: '#3f8cff', xhss: '#ffb020', xpss: '#ff4d5e' }

export type FieldGroup = 'Content' | 'Graphics' | 'Badges'

export type FieldDef = {
  key: string,
  label: string,
  group: FieldGroup,
  default?: boolean, // shown unless the user hides it; true when omitted
  available?: (data: ReportData) => boolean, // hidden from the list (and the report) when false
}

export type TemplateDef = {
  id: string,
  type: ReportType,
  name: string,
  description: string,
  component: ComponentType,
  fields: FieldDef[],
  // What "Template default" means for the background: a photo, or the template's own backdrop.
  defaultBackground: 'photo' | 'none',
  defaultAccent?: Accent,
  // The route map and power chart share one spot, so only one shows (the route, if both are on).
  // The designer offers them as a single choice.
  routeOrChart?: boolean,
  // Some templates need data only some activities have (a session leaderboard, ...).
  available?: (data: ReportData) => boolean,
  unavailableReason?: string,
}

export type ReportContext = {
  // The layout to draw, and the size of the area it's drawn in.
  size: LayoutSize,
  width: number,
  height: number,
  // The whole image, and where the layout area sits in it (only a story differs).
  frame: { width: number, height: number, top: number },
  data: ReportData,
  options: ReportOptions,
  fmt: Formatter,
  accent: string,
  background: string | null,
  show: (key: string) => boolean,
}

export function defaultOptions(def: TemplateDef): ReportOptions {
  return {
    fields: {},
    background: { kind: 'default', url: null },
    overlay: 55,
    trimRoute: true,
    colorPowerChart: true,
    chartHeartRate: false,
    powerMetric: 'rp',
    powerUnit: 'w',
    accent: def.defaultAccent ?? 'teal',
    size: DEFAULT_SIZE,
  }
}

/** Fill gaps in saved options (older saves, new fields) from the template defaults. */
export function normalizeOptions(def: TemplateDef, options?: Partial<ReportOptions> | null): ReportOptions {
  const base = defaultOptions(def)
  if (!options) return base
  return {
    fields: { ...(options.fields ?? {}) },
    background: options.background?.kind ? { kind: options.background.kind, url: options.background.url ?? null } : base.background,
    overlay: typeof options.overlay === 'number' ? options.overlay : base.overlay,
    trimRoute: typeof options.trimRoute === 'boolean' ? options.trimRoute : base.trimRoute,
    colorPowerChart: typeof options.colorPowerChart === 'boolean' ? options.colorPowerChart : base.colorPowerChart,
    chartHeartRate: typeof options.chartHeartRate === 'boolean' ? options.chartHeartRate : base.chartHeartRate,
    powerMetric: options.powerMetric && ['avg', 'mov', 'pedal', 'xep', 'rp'].includes(options.powerMetric) ? options.powerMetric : base.powerMetric,
    powerUnit: options.powerUnit === 'wkg' ? 'wkg' : base.powerUnit,
    accent: options.accent && ACCENTS[options.accent] ? options.accent : base.accent,
    size: sizeOf(options).size,
  }
}

// Stable per-activity pick from the stock photos, so the preview and the render agree.
function stockFor(data: ReportData): string | null {
  const stock = data.backgrounds.stock
  if (!stock.length) return null
  let hash = 0
  for (const c of data.activity.path) hash = (hash * 31 + c.charCodeAt(0)) >>> 0
  return stock[hash % stock.length]
}

export function resolveBackground(def: TemplateDef, data: ReportData, options: ReportOptions): string | null {
  const bg = options.background
  switch (bg.kind) {
    case 'none': return null
    case 'stock':
    case 'upload':
      return bg.url
    case 'streetview':
      // Each ride's own street view; rides without one get a stock photo instead.
      return data.backgrounds.streetview ?? stockFor(data)
    default:
      return def.defaultBackground === 'photo' ? (data.backgrounds.streetview ?? stockFor(data)) : null
  }
}

export function fieldAvailable(field: FieldDef, data: ReportData) {
  return !field.available || field.available(data)
}

export function createContext(def: TemplateDef, data: ReportData, options: ReportOptions): ReportContext {
  const fields = new Map(def.fields.map((f) => [f.key, f]))
  const { size, width, height } = sizeOf(options)
  const layout = size === 'story' ? REPORT_SIZES[STORY_LAYOUT] : REPORT_SIZES[size]
  return {
    size: size === 'story' ? STORY_LAYOUT : size,
    width: layout.width,
    height: layout.height,
    frame: { width, height, top: size === 'story' ? STORY_SAFE_TOP : 0 },
    data,
    options,
    fmt: formatter(data.units),
    accent: ACCENTS[options.accent]?.color ?? ACCENTS.teal.color,
    background: resolveBackground(def, data, options),
    show: (key: string) => {
      const field = fields.get(key)
      if (!field) return true
      if (!fieldAvailable(field, data)) return false
      return options.fields[key] ?? field.default ?? true
    },
  }
}

/** "Badges": the parts of other report types a template can mention. */
export function badgeFields(own: ReportType): FieldDef[] {
  const all: (FieldDef & { type: ReportType })[] = [
    { type: 'breakthrough', key: 'badge.breakthrough', label: 'Breakthrough', group: 'Badges', available: (d) => !!d.breakthrough },
    { type: 'group', key: 'badge.group', label: 'Group ride / session', group: 'Badges', available: (d) => !!d.group },
    { type: 'xfai', key: 'badge.xfai', label: 'Forecast AI plan', group: 'Badges', available: (d) => !!d.xfai },
    { type: 'workout', key: 'badge.workout', label: 'Workout', group: 'Badges', available: (d) => !!d.workout },
  ]
  return all.filter((f) => f.type !== own).map(({ type, ...f }) => f)
}

export function hexToRgb(hex: string): string {
  const h = hex.replace('#', '')
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16)
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`
}
